/**
 * Keyframed-parameter evaluation and editing (Fabled Edit Format v2).
 *
 * A `Param` is either a static number or `{ keyframes: [...] }`. Keyframe
 * time axis: SOURCE time for media clips (same axis as clip.in/out), clip-local
 * seconds for overlay clips — callers resolve the axis, this module is pure
 * math on whatever axis the keyframes use.
 *
 * Interpolation: the segment between keyframes k[i] and k[i+1] uses k[i]'s
 * easing ('hold' keeps k[i].value until k[i+1]). Before the first / after the
 * last keyframe the value clamps to that keyframe's value.
 */
import type {
  Easing,
  Effect,
  Keyframe,
  Param,
  ResolvedEffect,
  ResolvedTransform,
  Transform,
} from './types';

export function isAnimated(p: Param): p is { keyframes: Keyframe[] } {
  return typeof p !== 'number';
}

/** Keyframes of a param ([] when static). */
export function paramKeyframes(p: Param): Keyframe[] {
  return isAnimated(p) ? p.keyframes : [];
}

function ease(easing: Easing, u: number): number {
  switch (easing) {
    case 'linear':
      return u;
    case 'easeIn':
      return u * u;
    case 'easeOut':
      return 1 - (1 - u) * (1 - u);
    case 'hold':
      return 0;
  }
}

/** Value of a param at time t (on the param's keyframe axis). */
export function sampleParam(p: Param, t: number): number {
  if (!isAnimated(p)) return p;
  const kfs = p.keyframes;
  if (kfs.length === 0) return 0;
  if (t <= kfs[0].t) return kfs[0].value;
  const last = kfs[kfs.length - 1];
  if (t >= last.t) return last.value;
  for (let i = 0; i < kfs.length - 1; i++) {
    const a = kfs[i];
    const b = kfs[i + 1];
    if (t >= a.t && t < b.t) {
      const span = b.t - a.t;
      const u = span > 0 ? (t - a.t) / span : 1;
      return a.value + (b.value - a.value) * ease(a.easing, u);
    }
  }
  return last.value;
}

/** Transform with every param sampled at time t. */
export function sampleTransform(tr: Transform, t: number): ResolvedTransform {
  return {
    scale: sampleParam(tr.scale, t),
    x: sampleParam(tr.x, t),
    y: sampleParam(tr.y, t),
    rotate: sampleParam(tr.rotate, t),
    opacity: sampleParam(tr.opacity, t),
  };
}

/** Non-param bookkeeping keys on an Effect object. */
function isEffectMetaKey(k: string): boolean {
  return k === 'type' || k === 'id';
}

/** Effects with every param sampled at time t. */
export function sampleEffects(effects: Effect[], t: number): ResolvedEffect[] {
  return effects.map((e) => {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(e)) {
      if (k === 'id') continue; // render-time shape carries no id
      out[k] = k === 'type' ? v : sampleParam(v as Param, t);
    }
    return out as ResolvedEffect;
  });
}

/** Editing quantum — keyframes closer than this are the same keyframe. */
export const KEYFRAME_EPS = 1e-3;

/**
 * Set the param's value at time t: replaces a keyframe within KEYFRAME_EPS or
 * inserts a new one (sorted). On a static param this creates the first
 * keyframe, making the param animated.
 */
export function setParamAt(p: Param, t: number, value: number, easing: Easing = 'linear'): Param {
  const kfs = paramKeyframes(p)
    .filter((k) => Math.abs(k.t - t) > KEYFRAME_EPS)
    .map((k) => ({ ...k }));
  kfs.push({ t, value, easing });
  kfs.sort((a, b) => a.t - b.t);
  return { keyframes: kfs };
}

/**
 * Remove the keyframe at time t (within eps). Removing the last keyframe
 * collapses the param back to a static number (that keyframe's value).
 */
export function removeKeyframeAt(p: Param, t: number, eps = KEYFRAME_EPS): Param {
  if (!isAnimated(p)) return p;
  const kfs = p.keyframes.filter((k) => Math.abs(k.t - t) > eps);
  if (kfs.length === p.keyframes.length) return p;
  if (kfs.length === 0) {
    const nearest = p.keyframes.reduce((a, b) =>
      Math.abs(a.t - t) <= Math.abs(b.t - t) ? a : b,
    );
    return nearest.value;
  }
  return { keyframes: kfs.map((k) => ({ ...k })) };
}

/** Shift all keyframes by dt (overlay clips: re-anchor after a left trim). */
export function shiftKeyframes(p: Param, dt: number): Param {
  if (!isAnimated(p) || dt === 0) return p;
  return { keyframes: p.keyframes.map((k) => ({ ...k, t: k.t + dt })) };
}

/** Static representative value (first keyframe when animated). */
export function staticValue(p: Param): number {
  return isAnimated(p) ? (p.keyframes[0]?.value ?? 0) : p;
}

/**
 * Nearest keyframe times strictly before/after t across a set of params
 * (single axis). Null when none exists on that side.
 */
export function prevNextKeyframeTimes(
  params: Param[],
  t: number,
  eps = KEYFRAME_EPS,
): { prev: number | null; next: number | null } {
  let prev: number | null = null;
  let next: number | null = null;
  for (const p of params) {
    for (const k of paramKeyframes(p)) {
      if (k.t < t - eps && (prev === null || k.t > prev)) prev = k.t;
      if (k.t > t + eps && (next === null || k.t < next)) next = k.t;
    }
  }
  return { prev, next };
}

/** All params of an effect list (for navigation / validation walks). */
export function effectParams(effects: Effect[]): Param[] {
  const out: Param[] = [];
  for (const e of effects) {
    for (const [k, v] of Object.entries(e)) {
      if (!isEffectMetaKey(k)) out.push(v as Param);
    }
  }
  return out;
}

/** All params of a transform. */
export function transformParams(tr: Transform): Param[] {
  return [tr.scale, tr.x, tr.y, tr.rotate, tr.opacity];
}

// ---------------------------------------------------------------------------
// Clip-level param access (paramPath = 'transform.scale' |
// 'effects.<effectId>.amount' | 'position.x') and timeline-time →
// keyframe-axis conversion. Array segments resolve by element `id`, never by
// index — indices are not stable under concurrent insert/reorder.
// ---------------------------------------------------------------------------

interface ClipTimeFields {
  start: number;
  in?: number;
  out?: number;
  speed?: number;
  duration?: number;
}

/**
 * Convert a TIMELINE time to the clip's keyframe axis: source time for media
 * clips (clamped to [in, out]), clip-local time for overlays (clamped to
 * [0, duration]).
 */
export function clipParamTime(clip: ClipTimeFields, t: number): number {
  if (typeof clip.in === 'number' && typeof clip.out === 'number') {
    const src = clip.in + (t - clip.start) * (clip.speed ?? 1);
    return Math.max(clip.in, Math.min(clip.out, src));
  }
  const local = t - clip.start;
  const dur = clip.duration ?? Infinity;
  return Math.max(0, Math.min(dur, local));
}

function isParam(v: unknown): v is Param {
  return (
    typeof v === 'number' ||
    (typeof v === 'object' && v !== null && Array.isArray((v as { keyframes?: unknown }).keyframes))
  );
}

function resolvePath(clip: object, path: string): { parent: Record<string, unknown>; key: string } | null {
  const segs = path.split('.');
  let node: unknown = clip;
  for (let i = 0; i < segs.length - 1; i++) {
    if (typeof node !== 'object' || node === null) return null;
    if (Array.isArray(node)) {
      node = node.find(
        (el) => typeof el === 'object' && el !== null && (el as { id?: unknown }).id === segs[i],
      );
    } else {
      node = (node as Record<string, unknown>)[segs[i]];
    }
  }
  if (typeof node !== 'object' || node === null || Array.isArray(node)) return null;
  return { parent: node as Record<string, unknown>, key: segs[segs.length - 1] };
}

/** Every keyframable param of a clip (structural — avoids helper-module cycle). */
export function clipParams(clip: object): Param[] {
  const out: Param[] = [];
  const c = clip as {
    effects?: Effect[];
    transform?: Transform;
    position?: { x: Param; y: Param };
  };
  if (c.effects) out.push(...effectParams(c.effects));
  if (c.transform) out.push(...transformParams(c.transform));
  if (c.position) out.push(c.position.x, c.position.y);
  return out;
}

function mutateClipParams(clip: object, fn: (p: Param) => Param): void {
  const c = clip as {
    effects?: Effect[];
    transform?: Transform;
    position?: { x: Param; y: Param };
  };
  if (c.effects) {
    for (const e of c.effects) {
      for (const k of Object.keys(e)) {
        if (isEffectMetaKey(k)) continue;
        (e as unknown as Record<string, Param>)[k] = fn(
          (e as unknown as Record<string, Param>)[k],
        );
      }
    }
  }
  if (c.transform) {
    for (const k of ['scale', 'x', 'y', 'rotate', 'opacity'] as const) {
      c.transform[k] = fn(c.transform[k]);
    }
  }
  if (c.position) {
    c.position.x = fn(c.position.x);
    c.position.y = fn(c.position.y);
  }
}

/**
 * Move every keyframe (across ALL of the clip's params) sitting at axis time
 * `fromT` (within eps) to `toT`, keeping each param sorted. Used by the
 * timeline's clip-level diamond markers, which show the union of keyframe
 * times. Mutates the clip in place (callers operate on store drafts).
 */
export function retimeKeyframesAt(
  clip: object,
  fromT: number,
  toT: number,
  eps = KEYFRAME_EPS,
): void {
  mutateClipParams(clip, (p) => {
    if (!isAnimated(p)) return p;
    if (!p.keyframes.some((k) => Math.abs(k.t - fromT) <= eps)) return p;
    const kfs = p.keyframes.map((k) =>
      Math.abs(k.t - fromT) <= eps ? { ...k, t: toT } : { ...k },
    );
    kfs.sort((a, b) => a.t - b.t);
    return { keyframes: kfs };
  });
}

/** Union of keyframe axis times across all of a clip's params (sorted, eps-merged). */
export function clipKeyframeTimes(clip: object, eps = KEYFRAME_EPS): number[] {
  const times: number[] = [];
  for (const p of clipParams(clip)) {
    for (const k of paramKeyframes(p)) {
      if (!times.some((t) => Math.abs(t - k.t) <= eps)) times.push(k.t);
    }
  }
  return times.sort((a, b) => a - b);
}

/** Read the param at a path ('transform.scale', 'effects.0.amount', 'position.x'). */
export function getParamAtPath(clip: object, path: string): Param | null {
  const loc = resolvePath(clip, path);
  if (!loc) return null;
  const v = loc.parent[loc.key];
  return isParam(v) ? v : null;
}

/** Write the param at a path; returns false when the path doesn't resolve. */
export function setParamAtPath(clip: object, path: string, p: Param): boolean {
  const loc = resolvePath(clip, path);
  if (!loc || !isParam(loc.parent[loc.key])) return false;
  loc.parent[loc.key] = p;
  return true;
}
